import React, { useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import CustomTabs, { Tab } from "./CustomTabs";
import { useEventDivisions, usePaytables } from "../util/db";
import DivisionPayoutTab from "./DivisionPayoutTab";
import AllDivisionsPayoutTab from "./AllDivisionsPayoutTab";

function DivisionPayoutPools({
  eventId,
  scoringOrderGolf,
  throwScoringModeId,
  onSettingsReady,
}) {
  // State for per-division configuration
  const [divisionSettings, setDivisionSettings] = useState({});

  const {
    data: divisions,
    status: divisionsStatus,
    error: divisionsError,
  } = useEventDivisions(eventId);

  // Get available paytables to determine default paytable ID
  const { data: availablePaytables, status: paytablesStatus } = usePaytables();

  // Initialize default settings for each division when divisions load
  React.useEffect(() => {
    if (
      divisions &&
      divisions.length > 0 &&
      availablePaytables &&
      availablePaytables.length > 0
    ) {
      // Find default paytable ID (prefer "Am" or "Amateur", fallback to first available)
      const defaultPaytable =
        availablePaytables.find(
          (p) =>
            p.name.toLowerCase().includes("am") ||
            p.name.toLowerCase().includes("amateur")
        ) || availablePaytables[0];

      setDivisionSettings((prevSettings) => {
        const newSettings = { ...prevSettings };
        divisions.forEach((division) => {
          if (!newSettings[division.event_divisions_id]) {
            newSettings[division.event_divisions_id] = {
              payoutPercentage: 40, // Default to 40%
              selectedPaytableId: defaultPaytable.paytable_id, // Default to Amateur or first available
            };
          }
        });
        return newSettings;
      });
    }
  }, [divisions, availablePaytables]);

  // Helper function to get settings for a specific division
  const getDivisionSettings = React.useCallback(
    (divisionId) => {
      // Find default paytable ID if available
      const defaultPaytableId =
        availablePaytables && availablePaytables.length > 0
          ? (
              availablePaytables.find(
                (p) =>
                  p.name.toLowerCase().includes("am") ||
                  p.name.toLowerCase().includes("amateur")
              ) || availablePaytables[0]
            )?.paytable_id
          : null;

      const settings = divisionSettings[divisionId] || {
        payoutPercentage: 40,
        selectedPaytableId: defaultPaytableId,
      };

      return settings;
    },
    [divisionSettings, availablePaytables]
  );

  // Pass the current settings state directly to the parent component
  React.useEffect(() => {
    if (onSettingsReady) {
      onSettingsReady(divisionSettings);
    }
  }, [onSettingsReady, divisionSettings]);

  // Helper function to update settings for a specific division
  const updateDivisionSettings = (divisionId, updates) => {
    setDivisionSettings((prev) => {
      const newSettings = {
        ...prev,
        [divisionId]: {
          ...prev[divisionId],
          ...updates,
        },
      };
      return newSettings;
    });
  };

  if (divisionsStatus === "loading" || paytablesStatus === "loading") {
    return (
      <Card className="mb-3">
        <Card.Header style={{ background: "#2D083F", color: "white" }}>
          <Card.Title className="mb-0">Division Payout Pools</Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Loading divisions...</div>
        </Card.Body>
      </Card>
    );
  }

  if (divisionsError) {
    return (
      <Card className="mb-3">
        <Card.Header style={{ background: "#dc3545", color: "white" }}>
          <Card.Title className="mb-0">Error Loading Divisions</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-danger">
            <p>
              Unable to load division data. Please refresh the page and try
              again.
            </p>
            <small className="text-muted">
              Error: {divisionsError?.message}
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!divisions || divisions.length === 0) {
    return (
      <Card className="mb-3">
        <Card.Header style={{ background: "#2D083F", color: "white" }}>
          <Card.Title className="mb-0">Division Payout Pools</Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-3">
          <div className="text-muted">No divisions found for this event.</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card
      className="mb-4"
      style={{ borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
    >
      <Card.Header
        style={{
          background: "#2D083F",
          color: "white",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Card.Title className="mb-0">Payouts</Card.Title>
      </Card.Header>
      <Card.Body>
        <CustomTabs defaultActiveKey="all" showContainer={false}>
          {/* All Divisions Tab */}
          <Tab eventKey="all" title="All" key="all">
            <AllDivisionsPayoutTab
              eventId={eventId}
              divisions={divisions}
              scoringOrderGolf={scoringOrderGolf}
              throwScoringModeId={throwScoringModeId}
              divisionSettings={divisionSettings}
            />
          </Tab>
          {/* Individual Division Tabs */}
          {divisions.map((division) => {
            const settings = getDivisionSettings(division.event_divisions_id);
            return (
              <Tab
                eventKey={division.event_divisions_id}
                title={division.divisions.name}
                key={division.event_divisions_id}
              >
                <DivisionPayoutTab
                  eventId={eventId}
                  eventDivisionsId={division.event_divisions_id}
                  divisionName={division.divisions.name}
                  divisionData={division}
                  scoringOrderGolf={scoringOrderGolf}
                  throwScoringModeId={throwScoringModeId}
                  payoutPercentage={settings.payoutPercentage}
                  selectedPaytableId={settings.selectedPaytableId}
                  onUpdateSettings={(updates) =>
                    updateDivisionSettings(division.event_divisions_id, updates)
                  }
                />
              </Tab>
            );
          })}
        </CustomTabs>
      </Card.Body>
    </Card>
  );
}

export default DivisionPayoutPools;
